import { FC } from 'react';
import { Card } from "@/components/ui/card";
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface FinancialSummaryProps {
  data: Array<{
    month: string;
    income: number;
    expenses: number;
    net_income: number;
  }>;
}

export const FinancialSummary: FC<FinancialSummaryProps> = ({ data }) => {
  const currentMonthData = data[0] || {
    month: new Date().toISOString(),
    income: 0,
    expenses: 0,
    net_income: 0
  };

  const previousMonthData = data[1] || {
    income: 0,
    expenses: 0,
    net_income: 0
  };

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const incomeChange = calculatePercentageChange(currentMonthData.income, previousMonthData.income);
  const expensesChange = calculatePercentageChange(currentMonthData.expenses, previousMonthData.expenses);
  const netIncomeChange = calculatePercentageChange(currentMonthData.net_income, previousMonthData.net_income);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-6">
        Resumen de {format(parseISO(currentMonthData.month), 'MMMM yyyy', { locale: es })}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Ingresos</p>
            {incomeChange !== 0 && (
              <div className={`flex items-center text-sm ${incomeChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {incomeChange >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                {Math.abs(incomeChange).toFixed(1)}%
              </div>
            )}
          </div>
          <p className="text-2xl font-bold text-green-600">
            ${currentMonthData.income.toFixed(2)}
          </p>
        </div>
        
        <div className="p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Gastos</p>
            {expensesChange !== 0 && (
              <div className={`flex items-center text-sm ${expensesChange <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {expensesChange <= 0 ? <TrendingDown className="h-4 w-4 mr-1" /> : <TrendingUp className="h-4 w-4 mr-1" />}
                {Math.abs(expensesChange).toFixed(1)}%
              </div>
            )}
          </div>
          <p className="text-2xl font-bold text-red-600">
            ${currentMonthData.expenses.toFixed(2)}
          </p>
        </div>
        
        <div className="p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Balance</p>
            {netIncomeChange !== 0 && (
              <div className={`flex items-center text-sm ${netIncomeChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {netIncomeChange >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                {Math.abs(netIncomeChange).toFixed(1)}%
              </div>
            )}
          </div>
          <p className={`text-2xl font-bold ${currentMonthData.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${currentMonthData.net_income.toFixed(2)}
          </p>
        </div>
      </div>
    </Card>
  );
};